/**
 * All functions in this utility module are based on EmojiOne.
 */

import CategoriesJson from 'emojione/categories.json';
import EmojiJson from 'emojione/emoji.json';
import EmojiOne from 'emojione';


/**
 * @typedef {object} EmojiCategory
 * @property {string} id - ex. 'people'
 * @property {string} label - ex. 'Smileys & People'
 */

/**
 * @typedef {object} EmojiInfo
 * @property {string} value - UTF-16 string representation
 * @property {string} categoryId
 * @property {string} shortname - ex. ':grinning:'
 * @property {stirng} label - ex. 'grinning face'
 * @property {string} url
 */

/**
 * Returns a list of emoji categories, like "Smileys & People", "Animals &
 * Nature", etc. These categories can then be used to retrieve a list of
 * associated emojis using getEmojiByCategory().
 *
 * @returns {EmojiCategory[]}
 */
export function getCategories() {
    return CategoriesJson.slice().sort((a, b) => {
        return a.order - b.order;
    }).map(e1ToEmojiCategory);
}

/**
 * Returns a list of all known emoji.
 *
 * @returns {EmojiInfo[]}
 */
export function getAllEmoji() {
    return Object.values(EmojiJson).sort((a, b) => {
        return a.order - b.order;
    }).map(e1ToEmojiInfo);
}

/**
 * Returns a list of emoji belonging to the given category.
 *
 * @param {string} categoryId
 * @returns {EmojiInfo[]}
 */
export function getAllEmojiByCategory(categoryId) {
    return Object.values(EmojiJson).filter(emojiJson => {
        return emojiJson.category === categoryId;
    }).sort((a, b) => {
        return a.order - b.order;
    }).map(e1ToEmojiInfo);
}

/**
 * Returns a single emoji associated with the given unicode character. Returns
 * null if emoji not found.
 *
 * @param {string} unicode
 * @returns {EmojiInfo|null}
 */
export function getEmojiByUnicode(unicode) {
    const hex = stringToHexCodePoint(unicode);
    if (EmojiJson[hex]) {
        return e1ToEmojiInfo(EmojiJson[hex]);

    } else {
        // Try a more robust search
        const e1Emoji = Object.values(EmojiJson).find(emojiJson => {
            const alts = [
                emojiJson.code_points.base,
                emojiJson.code_points.fully_qualified,
                emojiJson.code_points.non_fully_qualified,
                emojiJson.code_points.output,
            ];
            return alts.includes(unicode);
        });
        if (e1Emoji) {
            return e1ToEmojiInfo(e1Emoji);
        } else {
            return null;
        }
    }
}

/**
 * Searchs emoji list by text and returns matching emojis.
 *
 * @param {string} searchText - ex. 'grin'
 * @returns {EmojiInfo[]}
 */
export function searchAllEmoji(searchText) {
    if (searchText.length === 0) {
        return getAllEmoji();
    } else {
        return Object.values(EmojiJson).filter(emojiJson => {
            return (emojiJson.name.includes(searchText) ||
                    emojiJson.shortname.includes(searchText));
        }).sort((a, b) => {
            return a.order - b.order;
        }).map(e1ToEmojiInfo);
    }
}

export default {
    getCategories,
    getAllEmoji,
    getAllEmojiByCategory,
    getEmojiByUnicode,
    searchAllEmoji,
};


/**
 * @param {string} hex - ex. '1f450', '1f450-1f3fb', ...
 * @returns {string}
 */
function hexCodePointToString(hex) {
    return String.fromCodePoint(...hex.split('-').map(s => parseInt(s, 16)));
}

/**
 * @param {string} str - ex. '😀'
 * @returns {string} ex. '1f450'
 */
function stringToHexCodePoint(str) {
    return Array.from(str).map(c => {
        return c.codePointAt(0).toString(16).padStart(4, '0');
    }).join('-');
}

/**
 * Converts EmojiOne category to our own format.
 *
 * @param {object} e1Category - found in CategoriesJson
 * @returns {EmojiCategory}
 */
function e1ToEmojiCategory(e1Category) {
    return {
        id: e1Category.category,
        label: e1Category.category_label,
    };
}

/**
 * Converts EmojiOne emoji to our own format.
 *
 * @param {object} e1Emoji - found in EmojiJson
 * @returns {EmojiInfo}
 */
function e1ToEmojiInfo(e1Emoji) {
    return {
        value: e1ToValue(e1Emoji),
        categoryId: e1Emoji.category,
        shortname: e1Emoji.shortname,
        label: e1Emoji.name,
        url: e1ToImageUrl(e1Emoji),
    };
}

/**
 * There are several code points to choose from within the e1Emoji structure;
 * this function picks just one to use.
 *
 * @param {object} e1Emoji
 * @returns {string}
 */
function e1ToValue(e1Emoji) {
    return hexCodePointToString(e1Emoji.code_points.base);
}

/**
 * There is a bug in our version of the library where one emoji isn't recognized
 * by the image function. This wrapper fixes that.
 *
 * See: https://github.com/joypixels/emojione/issues/512
 *
 * @param {object} e1Emoji - found in EmojiJson
 * @returns {string} url
 */
function e1ToImageUrl(e1Emoji) {
    const imgHtml = EmojiOne.toImage(e1Emoji.shortname);
    const regex = /src="(.*)"/;
    const match = imgHtml.match(regex);
    if (match) {
        return match[1];
    } else {
        // This is used only as a fallback because it doesn't work for the
        // general case.
        const baseUrl = `${EmojiOne.imagePathPNG}${EmojiOne.spriteSize}`;
        return `${baseUrl}/${e1Emoji.code_points.base}.png`;
    }
}



// WEBPACK FOOTER //
// ./src/utils/emoji.js